import { c as _c } from "react/compiler-runtime";
import { Avatar, Badge, Button, Card, HStack, IconButton, Separator, Text, VStack, Wrap } from '@chakra-ui/react';
import { MenuContent, MenuItem, MenuRoot, MenuSeparator, MenuTrigger } from '@src/components/chakra-ui/menu';
import { displayNameForUser } from '@src/components/sco/Account/UserAvatar';
import { UserChip } from '@src/components/sco/Account/UserChip';
import { OpenSeatAvatar } from '@src/components/sco/Advisors/cards/OpenSeatAvatar';
import { WIL_AVATAR, WIL_DISPLAY_NAME } from '@src/components/sco/Advisors/constants';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { DraftFastAgreementButton } from '@src/components/sco/Advisors/shared/DraftFastAgreementButton';
import { PaywalledAdvisorAction } from '@src/components/sco/Advisors/shared/PaywalledAdvisorAction';
import { SpecialtyTag } from '@src/components/sco/Advisors/shared/SpecialtyTag';
import { SeatData } from '@src/components/sco/Advisors/types';
import { seatAdvisorDisplayName, seatStatusPalette } from '@src/components/sco/Advisors/utils/engagementMappers';
import { WIL_DISPLAY } from '@src/components/sco/Advisors/utils/mapEngagements';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_ENGAGEMENT_SOURCE_TEAM } from '@src/constants/advisor-engagement-sources';
import { ADVISOR_ENGAGEMENT_STATUS_ACTIVE, ADVISOR_ENGAGEMENT_STATUS_DRAFT, ADVISOR_ENGAGEMENT_STATUS_INVITED, ADVISOR_ENGAGEMENT_STATUS_SEARCHING } from '@src/constants/advisor-engagement-statuses';
import { ScoAdvisorEngagement, ScoUser } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { LuEllipsis, LuTrash2 } from 'react-icons/lu';
type SeatCardProps = {
  seat: SeatData;
  seatId: string;
  engagementId: string;
  engagement: ScoAdvisorEngagement;
  canEdit?: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
};
const factItems = (seat: SeatData, includeCandidates: boolean): string[] => {
  const items = [...seat.compensationLabels, ...seat.cadenceLabels];
  if (includeCandidates) {
    items.push(seat.candidates);
  }
  return items.filter(Boolean);
};
const chipUserFromEngagement = (engagement: ScoAdvisorEngagement, fallbackName: string, isAI: boolean): ScoUser => {
  const user = engagement.advisorUser;
  const name = isAI ? WIL_DISPLAY_NAME : displayNameForUser(user) || fallbackName;
  return {
    ...(user ?? {}),
    id: user?.id || engagement.advisorUserId || engagement.advisorEngagementId || 'advisor',
    displayName: name,
    fullName: name,
    lastName: user?.lastName,
    pictureUrl: user?.pictureUrl
  } as ScoUser;
};
export const SeatCard = t0 => {
  const $ = _c(77);
  const {
    seat,
    seatId,
    engagementId,
    engagement,
    canEdit: t1,
    onDelete,
    isDeleting
  } = t0;
  const canEdit = t1 === undefined ? true : t1;
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const detailHref = `${paths.seats}/${seatId}`;
  const editHref = `${detailHref}/edit`;
  const statusKey = seat.statusKey;
  const hasAdvisor = seat.hasAdvisor;
  const isWil = !!engagement.isAI;
  let T0;
  let T1;
  let primaryHref;
  let primaryLabel;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== canEdit || $[1] !== detailHref || $[2] !== editHref || $[3] !== engagement || $[4] !== hasAdvisor || $[5] !== isWil || $[6] !== paths || $[7] !== router || $[8] !== seat || $[9] !== statusKey) {
    const advisorName = displayNameForUser(engagement.advisorUser, isWil) || seatAdvisorDisplayName(engagement) || (isWil ? WIL_DISPLAY_NAME : null) || WIL_DISPLAY_NAME;
    const advisorBio = isWil ? seat.advisorBio || WIL_DISPLAY.bio : seat.advisorBio;
    let t12;
    if ($[24] !== engagement.advisorUser?.userId || $[25] !== engagement.advisorUserId || $[26] !== paths) {
      t12 = paths.withQuery(paths.inbox, {
        advisorUserId: engagement.advisorUserId || engagement.advisorUser?.userId
      });
      $[24] = engagement.advisorUser?.userId;
      $[25] = engagement.advisorUserId;
      $[26] = paths;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    const messageHref = t12;
    const facts = factItems(seat, !hasAdvisor);
    const skills = hasAdvisor ? [] : seat.skills.slice(0, 2);
    let t13;
    if ($[28] !== hasAdvisor || $[29] !== seat.companyName || $[30] !== seat.title) {
      const subtitleParts = hasAdvisor ? [seat.title, seat.companyName] : ["Open board seat", seat.companyName];
      t13 = subtitleParts.filter(Boolean);
      $[28] = hasAdvisor;
      $[29] = seat.companyName;
      $[30] = seat.title;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    const subtitle = t13.join(" \xB7 ");
    primaryLabel = "View seat";
    primaryHref = detailHref;
    if (statusKey === ADVISOR_ENGAGEMENT_STATUS_DRAFT && canEdit) {
      primaryLabel = "Continue editing";
      primaryHref = editHref;
    } else {
      if (statusKey === ADVISOR_ENGAGEMENT_STATUS_SEARCHING) {
        if (seat.candidateCount > 0) {
          primaryLabel = seat.candidateCount === 1 ? "Review 1 application" : `Review ${seat.candidateCount} applications`;
          primaryHref = detailHref;
        } else {
          primaryLabel = "Find advisors";
          primaryHref = paths.directory;
        }
      } else {
        if ((statusKey === ADVISOR_ENGAGEMENT_STATUS_INVITED || statusKey === ADVISOR_ENGAGEMENT_STATUS_ACTIVE) && hasAdvisor && !isWil) {
          primaryLabel = "Message";
          primaryHref = messageHref;
        }
      }
    }
    T1 = Card.Root;
    t7 = "full";
    t8 = "hidden";
    t9 = "interactive";
    t10 = "full";
    if ($[32] !== detailHref || $[33] !== router) {
      t11 = () => router.push(detailHref);
      $[32] = detailHref;
      $[33] = router;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    T0 = Card.Body;
    t2 = 4;
    t3 = hasAdvisor ? isWil && !engagement.advisorUser ? <HStack align="start" flex={1} gap={3} minWidth={0}><Avatar.Root flexShrink={0} size="lg"><Avatar.Fallback name={WIL_DISPLAY_NAME} /><Avatar.Image src={WIL_AVATAR} /></Avatar.Root><VStack align="start" flex={1} gap={0.5} minWidth={0}><HStack align="start" gap={2} justify="space-between" width="full"><Text fontWeight="semibold" lineClamp={1}>{WIL_DISPLAY.name}</Text><Badge colorPalette={seatStatusPalette(statusKey)} flexShrink={0} variant="subtle">{seat.status}</Badge></HStack><Text color="colorPalette.fg/60" fontSize="sm" lineClamp={2}>{advisorBio}</Text></VStack></HStack> : <HStack align="start" flex={1} gap={3} minWidth={0}><UserChip stackProps={{
        align: "flex-start",
        flex: 1,
        minWidth: 0
      }} user={chipUserFromEngagement(engagement, advisorName, isWil)} userAvatarProps={{
        avatarProps: {
          boxShadow: "xs",
          size: "lg"
        },
        disablePopover: true,
        isAI: isWil,
        noLink: true
      }}>{subtitle ? <Text color="colorPalette.fg/55" fontSize="xs" lineClamp={1}>{subtitle}</Text> : null}{advisorBio ? <Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm" lineClamp={2}>{advisorBio}</Text> : null}{statusKey === ADVISOR_ENGAGEMENT_STATUS_INVITED ? <Text color="colorPalette.fg/60" fontSize="sm">Waiting on {advisorName} to accept</Text> : null}</UserChip><Badge colorPalette={seatStatusPalette(statusKey)} flexShrink={0} variant="subtle">{seat.status}</Badge></HStack> : <HStack align="start" flex={1} gap={3} minWidth={0}><OpenSeatAvatar /><VStack align="start" flex={1} gap={0.5} minWidth={0}><HStack align="start" gap={2} justify="space-between" width="full"><Text fontWeight="semibold" lineClamp={2}>{seat.title}</Text><HStack flexShrink={0} gap={1}>{seat.candidateCount > 0 ? <Badge colorPalette="orange" variant="subtle">{seat.candidateCount === 1 ? "1 application" : `${seat.candidateCount} applications`}</Badge> : null}<Badge colorPalette={seatStatusPalette(statusKey)} variant="subtle">{seat.status}</Badge></HStack></HStack><Text color="colorPalette.fg/55" fontSize="sm" lineClamp={1}>{subtitle}</Text></VStack></HStack>;
    if ($[35] !== hasAdvisor || $[36] !== seat.why) {
      t4 = !hasAdvisor && seat.why ? <Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm" lineClamp={2}>{seat.why}</Text> : null;
      $[35] = hasAdvisor;
      $[36] = seat.why;
      $[37] = t4;
    } else {
      t4 = $[37];
    }
    t5 = skills.length > 0 ? <Wrap gap={1.5}>{skills.map(skill => <SpecialtyTag key={skill} colorPalette={seat.palette} label={skill} />)}</Wrap> : null;
    t6 = facts.length > 0 ? <Text color="colorPalette.fg/55" fontSize="xs" lineClamp={2}>{facts.join(" \xB7 ")}</Text> : null;
    $[0] = canEdit;
    $[1] = detailHref;
    $[2] = editHref;
    $[3] = engagement;
    $[4] = hasAdvisor;
    $[5] = isWil;
    $[6] = paths;
    $[7] = router;
    $[8] = seat;
    $[9] = statusKey;
    $[10] = T0;
    $[11] = T1;
    $[12] = primaryHref;
    $[13] = primaryLabel;
    $[14] = t10;
    $[15] = t11;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    T0 = $[10];
    T1 = $[11];
    primaryHref = $[12];
    primaryLabel = $[13];
    t10 = $[14];
    t11 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  let t12;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Separator />;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== engagement.sourceKey || $[40] !== primaryHref || $[41] !== primaryLabel) {
    t13 = primaryLabel === "Message" ? <PaywalledAdvisorAction isTeamAdvisor={engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM} upgradeCtaButtonProps={{
      size: "xs"
    }}><Button asChild={true} size="xs" variant="cta"><NextLink href={primaryHref}>{primaryLabel}</NextLink></Button></PaywalledAdvisorAction> : <Button asChild={true} size="xs" variant="cta"><NextLink href={primaryHref}>{primaryLabel}</NextLink></Button>;
    $[39] = engagement.sourceKey;
    $[40] = primaryHref;
    $[41] = primaryLabel;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== canEdit || $[44] !== engagement || $[45] !== hasAdvisor) {
    t14 = canEdit && hasAdvisor ? <DraftFastAgreementButton compact={true} engagement={engagement} size="xs" variant="ghost" /> : null;
    $[43] = canEdit;
    $[44] = engagement;
    $[45] = hasAdvisor;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== canEdit || $[48] !== editHref || $[49] !== engagementId || $[50] !== isDeleting || $[51] !== onDelete || $[52] !== seat.title || $[53] !== statusKey) {
    t15 = (canEdit || onDelete) && <MenuRoot positioning={{
      placement: "bottom-end"
    }}><MenuTrigger asChild={true}><IconButton aria-label={`More actions for ${seat.title}`} size="xs" variant="ghost"><LuEllipsis /></IconButton></MenuTrigger><MenuContent minW="10rem">{canEdit && statusKey !== ADVISOR_ENGAGEMENT_STATUS_DRAFT ? <MenuItem asChild={true} value="edit"><NextLink href={editHref}>Edit seat</NextLink></MenuItem> : null}{canEdit && statusKey === ADVISOR_ENGAGEMENT_STATUS_DRAFT ? <MenuItem asChild={true} value="continue"><NextLink href={editHref}>Continue editing</NextLink></MenuItem> : null}{onDelete ? <>{canEdit ? <MenuSeparator /> : null}<MenuItem aria-label={`End seat ${engagementId}`} color="fg.error" disabled={isDeleting} value="end" onClick={() => onDelete()}><LuTrash2 />End seat</MenuItem></> : null}</MenuContent></MenuRoot>;
    $[47] = canEdit;
    $[48] = editHref;
    $[49] = engagementId;
    $[50] = isDeleting;
    $[51] = onDelete;
    $[52] = seat.title;
    $[53] = statusKey;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== t14 || $[56] !== t15) {
    t16 = <HStack gap={1}>{t14}{t15}</HStack>;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== t13 || $[59] !== t16) {
    t17 = <HStack align="center" gap={2} justify="space-between" onClick={_temp}>{t13}{t16}</HStack>;
    $[58] = t13;
    $[59] = t16;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  let t18;
  if ($[61] !== T0 || $[62] !== t17 || $[63] !== t2 || $[64] !== t3 || $[65] !== t4 || $[66] !== t5 || $[67] !== t6) {
    t18 = <T0 gap={t2}>{t3}{t4}{t5}{t6}{t12}{t17}</T0>;
    $[61] = T0;
    $[62] = t17;
    $[63] = t2;
    $[64] = t3;
    $[65] = t4;
    $[66] = t5;
    $[67] = t6;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== T1 || $[70] !== t10 || $[71] !== t11 || $[72] !== t18 || $[73] !== t7 || $[74] !== t8 || $[75] !== t9) {
    t19 = <T1 maxWidth={t7} overflow={t8} variant={t9} width={t10} onClick={t11}>{t18}</T1>;
    $[69] = T1;
    $[70] = t10;
    $[71] = t11;
    $[72] = t18;
    $[73] = t7;
    $[74] = t8;
    $[75] = t9;
    $[76] = t19;
  } else {
    t19 = $[76];
  }
  return t19;
};
function _temp(event) {
  return event.stopPropagation();
}